import {
  Card,
  CardContent,
  Divider,
  Stack,
  styled,
  Switch,
  Tooltip,
  Typography,
  type Theme,
} from '@mui/material';
import type { Condition, BackupPolicyConfig } from '@repo/api-gw-sdk';
import { BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import ButtonWithIcon from '@/components/buttons/buttonWithIcon';
import ConfirmationDialog from '@/components/dialogs/confirmationDialog';
import { Loader } from '@/components/layout/loading';
import OptionMenu from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import {
  getResourcesScopeType,
  type ResourcesScopeType,
} from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { Link } from '@/components/shared/link';
import VaultTag from '@/components/vaults/vaultTag';
import { useViewParameters } from '@/contexts/navigation';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';

import {
  BackupPolicyConditionTitle,
  isCustomPolicy,
  policySupportedProperties,
} from './BackupPolicyConditionEditor';
import BackupPolicyRetentionSchedulesTable from './BackupPolicyRetentionSchedulesTable';

import { createInventoryLink } from '../../inventory/[[...ids]]/inventoryCondition';

export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor:
      props.theme?.palette.mode === 'dark'
        ? props.disabled
          ? '#192a3570'
          : 'var(--mui-palette-background-paper)'
        : props.disabled
          ? 'rgba(255,255,255,0.5)'
          : '#fff',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '24px',
      paddingTop: 0,
    },
    ...props.style,
  };
});

interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfig;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfig) => void;
  onDelete?: (policy: BackupPolicyConfig) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfig) => void;
}

export default function BackupPolicyCard(props: BackupPolicyCardProps) {
  const enabled = props.policy.enabled;
  const { snackbar } = useWorkspace();
  const { isAuthorized } = useRoles();
  const { setParams } = useViewParameters();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(
    !!props.defaultExpanded
  );
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  const matchingVaults = props.vaults
    ?.filter((vault) =>
      props.policy?.schedules.find((schedule) => schedule.vaultId === vault.id)
    )
    .slice(0, 2);
  const resourcesScopeType: ResourcesScopeType = props.policy.condition
    ? getResourcesScopeType(props.policy.condition)
    : 'allIncluded';

  const togglePolicyEnabledState = () => {
    const enabled = props.policy.enabled;
    props.onEnabledPolicyUpdate?.(props.policy);
    snackbar.showMessage(
      <>
        <span className='font-semibold'>{props.policy.name}</span> backup policy
        is now {enabled ? 'disabled' : 'enabled'}.
      </>
    );
  };

  const onToggleClicked = () => {
    if (enabled) {
      setDisablePolicyDialogOpen(true);
    } else {
      togglePolicyEnabledState();
    }
  };

  const inventoryFilterCondition: Condition = {
    operator: ConditionOperatorEnum.ContainsAnyOf,
    value: [props.policy?.id || ''],
    property: backupPolicies.name,
  };

  return (
    <BPCard disabled={!enabled} data-testid='backup-policy-card'>
      <>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{
            margin: '12px 24px',
          }}
        >
          <div>
            <Typography variant='h3'>{props.policy.name}</Typography>
          </div>
          <Stack direction='row' alignItems='center' gap='16px'>
            {enabled && (
              <>
                {props.processing ? (
                  <Loader variant='inline' />
                ) : (
                  <Link
                    prefetch={false}
                    href={createInventoryLink([inventoryFilterCondition])}
                    onClick={() =>
                      setParams((state) => ({
                        ...state,
                        query: createCombineCondition([
                          inventoryFilterCondition,
                          createBackupStatusNotInCondition([
                            BackupStatus.ExcludedFromBackup,
                            BackupStatus.Disconnected,
                            BackupStatus.Unsupported,
                          ]),
                        ]),
                      }))
                    }
                    disabled={!isAuthorized('read:inventory')} // fix this to check read:backup-policies once directing to backupPolicyPage
                    style={{ color: 'var(--primary-color)' }}
                  >
                    <ButtonWithIcon
                      text={`${props.policy.attachedResources ?? 0} Resources`}
                      iconPosition='right'
                      icon='material-symbols-arrow-right-alt'
                    />
                  </Link>
                )}
                <Divider sx={{ height: '16px' }} orientation='vertical' />
              </>
            )}

            {props.onEdit && props.onDelete && (
              <OptionMenu
                options={[
                  {
                    testId: 'edit-policy',
                    text: 'Edit',
                    menuItemProps: {
                      disabled: !isAuthorized('update:policies'),
                      onClick: () => {
                        if (!props.policy) return;
                        props.onEdit?.(props.policy);
                      },
                    },
                  },
                  {
                    testId: 'delete-policy',
                    text: 'Delete',
                    menuItemProps: {
                      disabled: !isAuthorized('delete:policies'),
                      onClick: () => {
                        if (!props.policy) return;
                        props.onDelete?.(props.policy);
                      },
                    },
                  },
                ]}
              />
            )}
            {props.onEnabledPolicyUpdate && (
              <Tooltip
                title={
                  enabled ? 'This policy is enabled' : 'This policy is disabled'
                }
              >
                <Switch
                  disabled={!isAuthorized('update:policies')}
                  checked={enabled}
                  onChange={onToggleClicked}
                />
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <CardContent sx={{ marginTop: '24px' }}>
          <Accordion defaultExpanded={props.defaultExpanded}>
            <AccordionSummary>
              <Typography variant='h4'>Resource selection</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ResourcesSelectionViewer
                condition={props.policy.condition}
                conditionTitle={
                  <BackupPolicyConditionTitle
                    resourceTypes={props.policy.resourceTypes}
                  />
                }
                include={props.policy.include ?? []}
                exclude={props.policy.exclude ?? []}
                resourcesScopeType={resourcesScopeType}
                dynamicResourcesHeader={
                  <Typography variant='h4'>
                    Dynamic resource selection
                  </Typography>
                }
                allExcludedDescription={
                  'This backup policy won’t be automatically applied to any resources. Only manually included resources will be assigned to the policy.'
                }
                allIncludedDescription={
                  isCustomPolicy(props.policy.policyType)
                    ? 'This backup policy will be automatically applied to all resources of the selected types. When new ones are discovered, they’ll be included too.'
                    : 'This backup policy will be automatically applied to all resources. When new ones are discovered, they’ll be included too.'
                }
                supportProperties={policySupportedProperties.flat()}
                selectedResourceTypes={props.policy.resourceTypes}
              />
            </AccordionDetails>
          </Accordion>
          <Divider sx={{ margin: '12px -24px' }} />
          <Accordion
            expanded={vaultsSectionExpanded}
            onChange={() => setVaultsSectionExpanded(!vaultsSectionExpanded)}
          >
            <AccordionSummary>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                className='w-full'
              >
                <Typography variant='h4'>Backup schedules</Typography>
                {!vaultsSectionExpanded && (
                  <div>
                    {!!matchingVaults?.length &&
                      matchingVaults.map((vault) => (
                        <VaultTag key={vault.id} vault={vault} />
                      ))}
                  </div>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingRight: 0 }}>
              <BackupPolicyRetentionSchedulesTable
                policy={props.policy}
                vaults={props.vaults}
              />
            </AccordionDetails>
          </Accordion>
        </CardContent>
        <ConfirmationDialog
          isOpen={disablePolicyDialogOpen}
          title='Disable backup policy'
          description={
            <>
              The <span className='font-semibold'>{props.policy.name}</span>{' '}
              backup policy will stop being applied to resources. Existing
              snapshots will be kept until their retention expires.
            </>
          }
          onCancel={() => setDisablePolicyDialogOpen(false)}
          onConfirm={() => {
            togglePolicyEnabledState();
            setDisablePolicyDialogOpen(false);
          }}
          buttonTitle='Disable Policy'
        />
      </>
    </BPCard>
  );
}
